import { listOrganizations } from '@/client/organization';
import { useAmplifyApiClient } from '@/hooks/AmplifyApiClientHooks';
import {
  Organization,
  OrganizationGetByTenantQuery,
  UserCountByOrganizationQuery,
  UserCountsByOrganizationQuery
} from '../../API';
import {
  organizationGetByTenant,
  userCountByOrganization,
  userCountsByOrganization
} from '@/graphql/queries';
import { GraphQLQuery } from 'aws-amplify/api';
import { useAuth } from '@/providers/AuthProvider';
import config from '@/config';
import { ReactNode } from 'react';
import { createContext } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';

type OrgContextType = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  isEdit: boolean;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  editFocus: string;
  setEditFocus: React.Dispatch<React.SetStateAction<string>>;
  currentOrg: Organization;
  setCurrentOrg: React.Dispatch<React.SetStateAction<Organization>>;
  orgs: Organization[];
  queryOrgs: boolean;
  setQueryOrgs: React.Dispatch<React.SetStateAction<boolean>>;
};

type OrgContextProviderProps = {
  children: ReactNode;
};

const OrgContext = createContext<OrgContextType>({} as OrgContextType);

const OrgContextProvider = ({ children }: OrgContextProviderProps) => {
  const [queryOrgs, setQueryOrgs] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editFocus, setEditFocus] = useState('');
  const [currentOrg, setCurrentOrg] = useState<Organization>({
    id: '',
    name: '',
    createdAt: '',
    hostLicenses: 0,
    adminLicenses: 0,
    organizationActive: false,
    activeUsers: 0,
    activeHosts: 0,
    activeAdmins: 0,
    emailDomains: [],
    tenant: '',
    subdomain: '',
    groupsCanRead: [],
    updatedAt: '',
    __typename: 'Organization',
    onFreeTrial: false
  });
  const [orgs, setOrgs] = useState<Organization[]>([]);

  const client = useAmplifyApiClient();
  const { groups, tenant } = useAuth();
  const isSuperAdmin = groups.includes(config.admin.superAdminGroup);

  useEffect(() => {
    async function fetch() {
      // Fetch just their org if user is not a super admin
      if (!isSuperAdmin) {
        if (!tenant) {
          setOrgs([]);
          setQueryOrgs(false);
          return;
        }

        const resp = await client.graphql<GraphQLQuery<OrganizationGetByTenantQuery>>({
          query: organizationGetByTenant,
          variables: { tenant: tenant as string }
        });
        const org = resp.data?.organizationGetByTenant;

        if (org) {
          const countResp = await client.graphql<GraphQLQuery<UserCountByOrganizationQuery>>({
            query: userCountByOrganization,
            variables: { organizationId: org.id }
          });
          const count = countResp.data?.userCountByOrganization ?? {
            activeHosts: 0,
            activeAdmins: 0
          };
          setOrgs([
            {
              ...org,
              activeHosts: count.hostCount,
              activeAdmins: count.adminCount,
              activeUsers: count.hostCount + count.adminCount
            } as Organization
          ]);
        } else {
          setOrgs([]);
        }
        setQueryOrgs(false);
      } else {
        const orgs = (await listOrganizations()) as Organization[];
        const ids = (orgs || []).map((o) => o.id);
        const bulk = await client.graphql<GraphQLQuery<UserCountsByOrganizationQuery>>({
          query: userCountsByOrganization,
          variables: { organizationIds: ids }
        });
        const countsMap = new Map(
          (bulk.data?.userCountsByOrganization || []).map((c) => [c.organizationId, c.count])
        );
        setOrgs(
          (orgs || []).map((org) => ({
            ...org,
            activeUsers: countsMap.get(org.id) ?? 0
          })) as Organization[]
        );
        setQueryOrgs(false);
      }
    }

    setQueryOrgs(false);
    fetch();
  }, [client, queryOrgs, isSuperAdmin, tenant]);

  return (
    <OrgContext.Provider
      value={{
        showModal,
        setShowModal,
        isEdit,
        setIsEdit,
        editFocus,
        setEditFocus,
        currentOrg,
        setCurrentOrg,
        orgs,
        queryOrgs,
        setQueryOrgs
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export { OrgContext, OrgContextProvider };
